import { difference, isEqual, omitBy, isUndefined } from 'lodash'
import { parseUrl, parseUrlPattern, buildUrl, getUrlParts } from './utils'
import { INVALID_PATTERN_CHARS } from './invalid-chars-regex'
import type {
  ValidationResult,
  ValidationResultErrorParams,
} from '@wix/wix-data-client-types'
import { DynamicPageUrlValidationError } from '@wix/wix-data-client-types'
import { isFieldAllowedInRoutingUrl } from '../isFieldAllowedInRoutingUrl'
import { validatePrefixWithLatinOnly } from './validatePrefixWithLatinOnly'
import type {
  DigestedSchema,
  EditorSDK,
} from '@wix/wix-data-client-common-standalone'

const COLLECTION_FIELD_PLACEHOLDER = /\{[^{}]+\}/g
const ONLY_ONE_COLLECTION_FIELD_PLACEHOLDER = new RegExp(
  COLLECTION_FIELD_PLACEHOLDER.source +
    '[^/]*' +
    COLLECTION_FIELD_PLACEHOLDER.source,
  'g',
)
const MAX_URL_LENGTH = 500

const invalidResult = (
  errorKey: DynamicPageUrlValidationError,
  errorParams?: ValidationResultErrorParams,
): ValidationResult =>
  omitBy(
    { valid: false, error: errorKey, params: errorParams },
    isUndefined,
  ) as ValidationResult

export const arePatternsConflictingExactly = (
  pattern1: string,
  pattern2: string,
): boolean => {
  return isEqual(getUrlParts(pattern1), getUrlParts(pattern2))
}

export const arePatternsConflicting = (
  pattern1: string,
  pattern2: string,
): boolean => {
  const pattern1Parts = pattern1
    .replace(COLLECTION_FIELD_PLACEHOLDER, '#')
    .split('/')
    .filter(part => !!part)
  const pattern2Parts = pattern2
    .replace(COLLECTION_FIELD_PLACEHOLDER, '#')
    .split('/')
    .filter(part => !!part)
  return isEqual(pattern1Parts, pattern2Parts)
}

const validateUrlPrefix = async function (
  editorSdkProxy: EditorSDK,
  url: string,
): Promise<ValidationResult> {
  const { prefix } = parseUrl(url)
  if (prefix.startsWith('{')) {
    return invalidResult(DynamicPageUrlValidationError.PREFIX_STARTS_WITH_TEXT)
  }
  if (prefix.match(COLLECTION_FIELD_PLACEHOLDER)) {
    return invalidResult(DynamicPageUrlValidationError.NO_FIELD_IN_PREFIX)
  }
  const isValidPrefix = await validatePrefixWithLatinOnly({
    prefix,
    editorSdkProxy,
  })
  if (!isValidPrefix.valid) {
    return { valid: false, error: isValidPrefix.message }
  }

  return { valid: true }
}

export const validateUrlPattern = function (
  url: string,
  collection: DigestedSchema,
  existingPatterns: string[],
  oldPattern = '',
  editPanelFlow?: boolean,
): ValidationResult {
  const { prefix, pattern } = parseUrl(url)
  const { fields: urlFieldNames } = parseUrlPattern(pattern)
  const { fields: oldUrlFieldNames } = parseUrlPattern(oldPattern)
  const urlFieldsFromCollection = collection.fields.filter(field =>
    urlFieldNames.includes(field.name),
  )

  const patternWithoutFields = pattern.replace(COLLECTION_FIELD_PLACEHOLDER, '')
  if (patternWithoutFields.match(INVALID_PATTERN_CHARS)) {
    return invalidResult(DynamicPageUrlValidationError.NO_SPECIAL_CHARS)
  }
  if (
    urlFieldsFromCollection.some(
      field => !isFieldAllowedInRoutingUrl(collection, field.name),
    )
  ) {
    return invalidResult(DynamicPageUrlValidationError.UNSUPPORTED_FIELDS)
  }
  if (urlFieldsFromCollection.some(field => field.isDeleted)) {
    return invalidResult(DynamicPageUrlValidationError.NO_DELETED_FIELD)
  }
  const existingFieldNames = collection.fields
    .filter(({ isDeleted }) => !isDeleted)
    .map(({ name }) => name)
  if (difference(urlFieldNames, existingFieldNames).length > 0) {
    return invalidResult(DynamicPageUrlValidationError.ONLY_EXISTING_FIELDS)
  }

  const conflictingExactPattern = existingPatterns.some(existingPattern =>
    arePatternsConflictingExactly(existingPattern, pattern),
  )
  if (editPanelFlow && conflictingExactPattern) {
    return invalidResult(DynamicPageUrlValidationError.EXACT_PATTERN_EXISTS)
  }

  const conflictingPattern = existingPatterns.find(existingPattern =>
    arePatternsConflicting(existingPattern, pattern),
  )
  if (conflictingPattern) {
    return invalidResult(DynamicPageUrlValidationError.PATTERN_ALREADY_EXISTS, {
      conflicting_url: buildUrl(prefix, conflictingPattern),
    })
  }

  if (pattern.match(ONLY_ONE_COLLECTION_FIELD_PLACEHOLDER)) {
    return invalidResult(DynamicPageUrlValidationError.SEPARATE_FIELDS)
  }

  if (
    editPanelFlow &&
    oldUrlFieldNames.length > 0 &&
    urlFieldNames.length === 0
  ) {
    return { valid: true, warning: DynamicPageUrlValidationError.NO_VARIABLE }
  }
  return { valid: true }
}

export const validateUrl = async function (
  editorSdkProxy: EditorSDK,
  url: string,
  collection: DigestedSchema,
  existingPatterns: string[] = [],
  oldPattern: string,
  editPanelFlow?: boolean,
): Promise<ValidationResult> {
  const { prefix, pattern } = parseUrl(url)

  if (url === '') {
    return invalidResult(DynamicPageUrlValidationError.URL_EMPTY)
  }

  if (url.length > MAX_URL_LENGTH) {
    return invalidResult(DynamicPageUrlValidationError.URL_TOO_LONG, {
      max_length: MAX_URL_LENGTH,
    })
  }

  if (prefix && prefix.length > 0) {
    const prefixValidity = await validateUrlPrefix(editorSdkProxy, url)
    if (!prefixValidity.valid) {
      return prefixValidity
    }
  }

  if (pattern && pattern.length > 0) {
    const patternValidity = validateUrlPattern(
      url,
      collection,
      existingPatterns,
      oldPattern,
      editPanelFlow,
    )
    if (!patternValidity.valid || patternValidity.warning) {
      return patternValidity
    }
  }

  return { valid: true }
}
